import React, { useState } from 'react'
import { Card, CardHeader, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import {
  BarChart3,
  UsersIcon,
  CalendarIcon,
  DollarSign,
  DownloadIcon,
  Filter
} from 'lucide-react'

export const ReportsPage: React.FC = () => {
  const [selectedReport, setSelectedReport] = useState<string>('overview')

  const reports = [
    {
      id: 'overview',
      name: 'Översikt',
      description: 'Generell statistik och översikt',
      icon: BarChart3,
      available: true,
    },
    {
      id: 'attendance',
      name: 'Närvaro',
      description: 'Närvarostatistik och deltagande',
      icon: UsersIcon,
      available: true,
    },
    {
      id: 'revenue',
      name: 'Intäkter',
      description: 'Ekonomisk rapportering',
      icon: DollarSign,
      available: false,
    },
    {
      id: 'utilization',
      name: 'Kapacitetsutnyttjande',
      description: 'Träningarnas fyllnadsgrad',
      icon: CalendarIcon,
      available: false,
    },
  ]

  const mockStats = {
    totalTrainings: 145,
    totalParticipants: 892,
    averageAttendance: 85,
    totalRevenue: 45600,
    popularTime: '18:30',
    mostActiveDay: 'Torsdag',
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Rapporter</h1>
          <p className="text-gray-600">Statistik och analyser av träningar</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline">
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </Button>
          <Button variant="outline">
            <DownloadIcon className="h-4 w-4 mr-2" />
            Exportera
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Report Selection Sidebar */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <h3 className="text-lg font-medium text-gray-900">Rapporter</h3>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {reports.map((report) => (
                <button
                  key={report.id}
                  onClick={() => setSelectedReport(report.id)}
                  disabled={!report.available}
                  className={`w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-colors ${
                    selectedReport === report.id
                      ? 'bg-primary-50 border-primary-200'
                      : report.available
                        ? 'hover:bg-gray-50 border-transparent'
                        : 'opacity-50 cursor-not-allowed'
                  }`}
                >
                  <report.icon className={`h-5 w-5 ${
                    selectedReport === report.id ? 'text-primary-600' : 'text-gray-400'
                  }`} />
                  <div>
                    <p className={`text-sm font-medium ${
                      selectedReport === report.id ? 'text-primary-900' : 'text-gray-900'
                    }`}>
                      {report.name}
                    </p>
                    <p className="text-xs text-gray-500">{report.description}</p>
                  </div>
                  {!report.available && (
                    <Badge variant="warning" className="ml-auto text-xs">
                      Kommer snart
                    </Badge>
                  )}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Report Content */}
        <div className="lg:col-span-3">
          {selectedReport === 'overview' && (
            <div className="space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Totalt träningar</p>
                        <p className="text-2xl font-bold text-gray-900">{mockStats.totalTrainings}</p>
                      </div>
                      <CalendarIcon className="h-8 w-8 text-primary-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Totalt deltagare</p>
                        <p className="text-2xl font-bold text-gray-900">{mockStats.totalParticipants}</p>
                      </div>
                      <UsersIcon className="h-8 w-8 text-success-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Snitt närvaro</p>
                        <p className="text-2xl font-bold text-gray-900">{mockStats.averageAttendance}%</p>
                      </div>
                      <BarChart3 className="h-8 w-8 text-warning-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Totala intäkter</p>
                        <p className="text-2xl font-bold text-gray-900">{mockStats.totalRevenue}kr</p>
                      </div>
                      <DollarSign className="h-8 w-8 text-green-600" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Additional Insights */}
              <Card>
                <CardHeader>
                  <h3 className="text-lg font-medium text-gray-900">Insikter</h3>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Populäraste tiden</h4>
                      <p className="text-2xl font-bold text-primary-600">{mockStats.popularTime}</p>
                      <p className="text-sm text-gray-600">Flest anmälningar</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Mest aktiva dagen</h4>
                      <p className="text-2xl font-bold text-primary-600">{mockStats.mostActiveDay}</p>
                      <p className="text-sm text-gray-600">Högst deltagande</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {selectedReport === 'attendance' && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-medium text-gray-900">Närvarorapport</h3>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex">
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">
                          Rapportfunktion under utveckling
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <p>
                            Detaljerade närvarorapporter kommer snart. För tillfället kan du använda översiktsstatistiken ovan.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {(selectedReport === 'revenue' || selectedReport === 'utilization') && (
            <Card>
              <CardHeader>
                <h3 className="text-lg font-medium text-gray-900">Kommer snart</h3>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <BarChart3 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Denna rapport är under utveckling
                  </h3>
                  <p className="text-gray-500 mb-6">
                    Vi arbetar på att lägga till denna funktionalitet snart.
                  </p>
                  <Button onClick={() => setSelectedReport('overview')}>
                    Tillbaka till översikt
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
